import { useEffect, useState } from "react";
import { Input } from "antd";
import {
  useAllOrders,
  useSearchOrders,
} from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const Delivered = ({ status = "delivered" }) => {
  const { setLoading } = useLoader();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Search orders API (tab=delivered)
  const {
    data: searchedData = [],
    isLoading: loadingSearch,
    refetch: refetchSearch,
  } = useSearchOrders(debouncedSearchText, "delivered");

  // Delivered orders API (with pagination, tab=delivered)
  const {
    data: allOrders = { orders: [], totalPages: 1 },
    isLoading: loadingAll,
    isFetching,
    isError,
    refetch,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    status,
  });

  const isLoading = debouncedSearchText ? loadingSearch : loadingAll;
  const orders = debouncedSearchText ? searchedData : allOrders.orders || [];
  const totalPages = debouncedSearchText ? 1 : allOrders.totalPages || 1;

  useEffect(() => {
    setLoading(true);
    refetch().finally(() => setLoading(false));
  }, [refetch, setLoading]);

  if (isError) {
    return (
      <p className="text-red-600 mt-4">Error fetching delivered orders.</p>
    );
  }

  return (
    <div className="w-full mt-6">
      {/* Search Input */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1000px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">AWB Number / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer Name / Contact</th>
              <th className="p-3">Address</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-500">
                  Loading delivered orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              <>
                {orders.map((order, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="p-4">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-blue-600">
                        {order.awbNumber}
                      </div>
                      <div
                        className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                          order.shippingStatus === "cancelled"
                            ? "bg-red-100 text-red-600"
                            : order.shippingStatus === "in_transit"
                            ? "bg-blue-100 text-blue-600"
                            : order.shippingStatus === "shipped"
                            ? "bg-green-100 text-green-600"
                            : order.shippingStatus === "delivered"
                            ? "bg-purple-100 text-purple-600"
                            : "bg-yellow-100 text-yellow-600"
                        }`}
                      >
                        {order.shippingStatus}
                      </div>
                    </td>
                    <td className="p-4">
                      <p>₹{order.total_amount}</p>
                      <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1">
                        {order.PaymentType}
                      </span>
                    </td>
                    <td className="p-4">
                      {order.ProductDetails?.map((prod, i) => (
                        <p key={i} className="truncate">
                          {prod.name}
                        </p>
                      ))}
                    </td>
                    <td className="p-4">
                      <p>{order.CustomerName}</p>
                      <p>{order.BillingAddress?.phone}</p>
                    </td>
                    <td className="p-4 text-sm">
                      <p>{order.BillingAddress?.city}</p>
                      <p>{order.BillingAddress?.state}</p>
                      <p>{order.BillingAddress?.pincode}</p>
                    </td>
                  </tr>
                ))}
                {isFetching && !debouncedSearchText && (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-blue-500">
                      Loading more orders...
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-400">
                  No delivered orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!debouncedSearchText && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Delivered;
